/**
 * Frontend mirror of the backend reconciliation types
 * (backend/src/types/invoicing.ts + reconciliationReportFirestore.ts).
 */

export type ReconciliationSeverity = 'ok' | 'warning' | 'critical';

export type ReconciliationCheckKey =
  | 'unbilledIo'
  | 'unissuedInvoice'
  | 'underBilling'
  | 'underBooking'
  | 'invoiceMismatch'
  | 'billedNotInvoiced'
  | 'plannedNotBilled'
  | 'deliveredOverBilled'
  | 'untrackedInvoice';

export interface ReconciliationCheckResult {
  key: ReconciliationCheckKey;
  flagged: boolean;
  severity: ReconciliationSeverity;
  detail: string;
  dollarsAtRisk?: number;
}

/** A billing month is 'mature' once it is due (closed + invoicing lag); 'pending' months
 *  are not yet due and are excluded from dollars-at-risk. */
export type MonthMaturity = 'mature' | 'pending';

/** One billing MONTH of a campaign — the grain the engine reconciles at, rolled up into
 *  the campaign finding. `delivered*` are informational and never drive risk. */
export interface MonthlyReconciliationRow {
  monthKey: string;
  monthlyFlightStart?: string;
  monthlyFlightEnd?: string;
  planned?: number;
  contractedImpressions?: number;
  expected?: number;
  deliveredImpressions?: number;
  deliveredValue?: number;
  billed?: number;
  invoiced?: number;
  invoiceNumbers?: string[];
  /** The tracker's free-text status for this month (e.g. "Paid", "Unpaid") — display-only. */
  status?: string;
  maturity: MonthMaturity;
  matchQuality?: 'order' | 'fuzzy' | 'none';
  checks: ReconciliationCheckResult[];
  worstSeverity: ReconciliationSeverity;
  dollarsAtRisk: number;
}

export interface ReconciliationFinding {
  id: string;
  source: 'invoice' | 'io-only' | 'invoice-only';
  campaignKey?: string;
  advertiser: string;
  agency?: string;
  orderNumber?: string;
  campaignOrderId?: string;
  ioLink?: string;
  ioFileName?: string;
  monthKeys?: string[];
  flightStart?: string;
  flightEnd?: string;
  contractedImpressions?: number;
  bookedImpressions?: number;
  deliveredImpressions?: number;
  cpm?: number;
  contractedValue?: number;
  deliveredValue?: number;
  billedValue?: number;
  invoicedValue?: number;
  invoiceNumbers?: string[];
  /** Per-billing-month ledger (sorted; absent on io-only findings). */
  months?: MonthlyReconciliationRow[];
  /** Flight ended AND every month mature — only then is whole-contract under-billing trustworthy. */
  complete?: boolean;
  matureMonths?: number;
  pendingMonths?: number;
  checks: ReconciliationCheckResult[];
  worstSeverity: ReconciliationSeverity;
  /** Σ of MATURE-month risks + IO-level risks (pending excluded). */
  totalDollarsAtRisk: number;
  recommendation: string;
}

export interface ReconciliationReport {
  generatedAt: string;
  window: { minDays: number; maxDays: number; startDate: string; endDate: string };
  scannedMonths: string[];
  /** False on cached runs — the invoice-PDF cross-check didn't run (absent on old reports). */
  scannedInvoicePdfs?: boolean;
  totals: {
    campaignsInWindow: number;
    iosScanned: number;
    findings: number;
    bySeverity: Record<ReconciliationSeverity, number>;
    byCheck: Record<ReconciliationCheckKey, number>;
    totalDollarsAtRisk: number;
    pendingDollarsDeferred?: number;
    monthsBilledNotInvoiced?: number;
    monthsPlannedNotBilled?: number;
    underBilledCompleteCampaigns?: number;
  };
  findings: ReconciliationFinding[];
  sheetUrl?: string;
  notes: string[];
}

export interface ReconciliationRunState {
  status: 'running' | 'complete' | 'failed';
  startedAt: string;
  finishedAt?: string;
  requestedBy?: string;
  window?: { minDays: number; maxDays: number };
  report?: ReconciliationReport;
  error?: string;
}

export interface ReconciliationStatus {
  canView: boolean;
  canManage: boolean;
  inFlight: boolean;
}

export const CHECK_LABEL: Record<ReconciliationCheckKey, string> = {
  unbilledIo: 'Un-billed IO',
  unissuedInvoice: 'Un-issued invoice',
  underBilling: 'Under-billing',
  underBooking: 'Under-booking/delivery',
  invoiceMismatch: 'Invoice vs tracker',
  billedNotInvoiced: 'Billed, not invoiced',
  plannedNotBilled: 'Planned, not billed',
  deliveredOverBilled: 'Delivered > billed (review)',
  untrackedInvoice: 'Untracked invoice',
};

export const MONTH_MATURITY_LABEL: Record<MonthMaturity, string> = {
  mature: 'Due',
  pending: 'Not yet due',
};
